import { useState } from "react";
import { Card, Form, Input, Button, Alert, Typography, Space, Result } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { Link, useParams, useNavigate } from "react-router-dom";
import { resetPassword } from "@/api/auth";

const { Title, Text } = Typography;

interface ResetForm {
  newPassword: string;
  confirm: string;
}

export function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <Result status="warning" title="Thiếu token reset"
        extra={<Link to="/forget-password">Yêu cầu link mới</Link>}
      />
    );
  }

  const handleSubmit = async (values: ResetForm) => {
    setLoading(true);
    setError(null);
    try {
      await resetPassword(token, values.newPassword);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      setError(JSON.stringify(detail) || "Đặt lại mật khẩu thất bại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
      }}
    >
      <Card style={{ width: 420, padding: "16px 8px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              Đặt lại mật khẩu
            </Title>
            <Text type="secondary">Mật khẩu mới phải có ≥ 10 ký tự.</Text>
          </div>

          {success ? (
            <Alert
              type="success"
              message="Thành công"
              description="Mật khẩu đã được đặt lại. Đang chuyển về trang đăng nhập..."
              showIcon
            />
          ) : (
            <>
              {error && <Alert type="error" message={error} showIcon closable />}
              <Form<ResetForm> layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                <Form.Item
                  name="newPassword"
                  label="Mật khẩu mới"
                  rules={[
                    { required: true, message: "Vui lòng nhập mật khẩu mới." },
                    { min: 10, message: "Tối thiểu 10 ký tự." },
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} size="large" />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label="Xác nhận mật khẩu"
                  dependencies={["newPassword"]}
                  rules={[
                    { required: true, message: "Vui lòng xác nhận mật khẩu." },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Mật khẩu không khớp."));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} size="large" />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" loading={loading} block size="large">
                    Đặt lại mật khẩu
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default ResetPassword;
